import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import css from './AiPointerButton.module.css';
import type { PointerHelpSource } from './types';
import {
  POINTER_BUTTON_ATTRIBUTE,
  POINTER_CLOSED_EVENT,
  POINTER_HOVER_EVENT,
  POINTER_TOGGLE_EVENT,
  type PointerAnchor,
  type PointerHoverDetail,
} from './open-event';
import { hasSeen, markSeen } from './seen';
import { SparkleIcon } from './SparkleIcon';

const BUTTON_SIZE = 28;
const AUTO_OPEN_DELAY_MS = 500;
const AFTER_CLOSE_WAIT_MS = 1200;

export type AutoOpen = {
  id: string;
  enabled?: boolean;
  afterClose?: boolean;
};

const TEXTAREA_ROW_CENTER = 31;

function getAnchor(
  button: HTMLElement,
  help?: PointerHelpSource,
): PointerAnchor {
  const rect = button.getBoundingClientRect();
  return {
    help,
    x: rect.left,
    y: rect.bottom - 8,
    originX: rect.left + rect.width / 2,
    originY: rect.top + rect.height / 2,
  };
}

function dispatchToggle(
  button: HTMLElement,
  help?: PointerHelpSource,
  isAutomatic?: boolean,
) {
  window.dispatchEvent(
    new CustomEvent<PointerAnchor>(POINTER_TOGGLE_EVENT, {
      detail: { ...getAnchor(button, help), isAutomatic },
    }),
  );
}

function dispatchHover(
  button: HTMLElement,
  hovering: boolean,
  help?: PointerHelpSource,
) {
  window.dispatchEvent(
    new CustomEvent<PointerHoverDetail>(POINTER_HOVER_EVENT, {
      detail: { ...getAnchor(button, help), hovering },
    }),
  );
}

export function AiPointerButton({
  inline = false,
  help,
  autoOpen,
}: {
  inline?: boolean;
  help?: PointerHelpSource;
  autoOpen?: AutoOpen;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const [top, setTop] = useState<number | undefined>();
  const [isDismissed, setIsDismissed] = useState(false);
  const didAutoOpen = useRef(false);
  const helpRef = useRef(help);
  helpRef.current = help;
  const autoOpenId = autoOpen?.enabled === false ? undefined : autoOpen?.id;
  const autoOpenAfterClose = autoOpen?.afterClose;

  useEffect(() => {
    if (!autoOpenId) return undefined;
    if (hasSeen('auto', autoOpenId)) {
      setIsDismissed(true);
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const open = () => {
      if (!ref.current) return;
      didAutoOpen.current = true;
      dispatchToggle(ref.current, helpRef.current, true);
    };
    if (!autoOpenAfterClose) {
      timer = setTimeout(open, AUTO_OPEN_DELAY_MS);
      return () => clearTimeout(timer);
    }
    let otherOpened = false;
    const onOtherOpened = () => {
      otherOpened = true;
    };
    const onOtherClosed = () => {
      window.removeEventListener(POINTER_CLOSED_EVENT, onOtherClosed);
      clearTimeout(timer);
      timer = setTimeout(open, AUTO_OPEN_DELAY_MS / 2);
    };
    window.addEventListener(POINTER_TOGGLE_EVENT, onOtherOpened);
    window.addEventListener(POINTER_CLOSED_EVENT, onOtherClosed);
    timer = setTimeout(() => {
      window.removeEventListener(POINTER_TOGGLE_EVENT, onOtherOpened);
      if (otherOpened) return;
      window.removeEventListener(POINTER_CLOSED_EVENT, onOtherClosed);
      open();
    }, AFTER_CLOSE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(POINTER_TOGGLE_EVENT, onOtherOpened);
      window.removeEventListener(POINTER_CLOSED_EVENT, onOtherClosed);
    };
  }, [autoOpenId, autoOpenAfterClose]);

  useEffect(() => {
    const onClosed = () => {
      if (!didAutoOpen.current || !autoOpenId) return;
      markSeen('auto', autoOpenId);
      setIsDismissed(true);
    };
    window.addEventListener(POINTER_CLOSED_EVENT, onClosed);
    return () => window.removeEventListener(POINTER_CLOSED_EVENT, onClosed);
  }, [autoOpenId]);

  useLayoutEffect(() => {
    const host = ref.current?.parentElement;
    const input = host?.querySelector('input, textarea');
    if (inline || !host) return undefined;
    if (!input) {
      const center = () => setTop((host.offsetHeight - BUTTON_SIZE) / 2);
      center();
      const observer = new ResizeObserver(center);
      observer.observe(host);
      return () => observer.disconnect();
    }
    const align = () => {
      const hostRect = host.getBoundingClientRect();
      const inputRect = input.getBoundingClientRect();
      const center =
        input instanceof HTMLTextAreaElement
          ? inputRect.bottom - TEXTAREA_ROW_CENTER
          : inputRect.top + inputRect.height / 2;
      setTop(center - hostRect.top - BUTTON_SIZE / 2);
    };
    align();
    const observer = new ResizeObserver(align);
    observer.observe(host);
    observer.observe(input);
    input.addEventListener('input', align);
    input.addEventListener('focus', align);
    input.addEventListener('blur', align);
    return () => {
      observer.disconnect();
      input.removeEventListener('input', align);
      input.removeEventListener('focus', align);
      input.removeEventListener('blur', align);
    };
  }, []);

  if (isDismissed) return null;

  return (
    <button
      ref={ref}
      style={inline ? undefined : { top }}
      onMouseDown={(event) => event.preventDefault()}
      type="button"
      className={inline ? `${css.button} ${css.inline}` : css.button}
      aria-label="Ask Phin for help"
      {...{ [POINTER_BUTTON_ATTRIBUTE]: '' }}
      onMouseEnter={(event) => dispatchHover(event.currentTarget, true, help)}
      onMouseLeave={(event) => dispatchHover(event.currentTarget, false, help)}
      onClick={(event) => dispatchToggle(event.currentTarget, help)}
    >
      <SparkleIcon size={14} />
    </button>
  );
}
